import React, { createContext, useState, useEffect, useContext } from 'react';
import { ApiServicesContext } from './ApiServicesContext';
import { DEFAULT_MAP_OPTIONS, DEFAULT_PUBLIC_MAP_OPTIONS_COLOR } from '../utils/defaults';
import { isEmpty } from 'lodash';
import { FEATURE_CONSTANTS } from 'utils/constants';
import { hash } from 'utils/helpers';
import { getPublicGISLayers } from '../utils/gisapi';
import SnackbarUtils from 'utils/SnackbarUtils';

export const PublicmapContext = createContext({
  publicGISLayers: [],
  publicmapOption: null,
  publicmapColorOption: null,
  colorBlindMode: false,
  customerContact: null,
  materials: [],
  publicmapDisclaimer: null,
  enabledFeatures: null,
  tenantName: null,
  tenantDisplayName: null,
  replacementSignUpEnabled: false,
  tenantLicenseLevel: null,
});

const PublicmapContextProvider = ({ children, apiUrl, gisLayerURL, tenantName }) => {
  const { mapService, publicService } = useContext(ApiServicesContext);
  const initialState = {
    publicGISLayers: [],
    publicmapOption: {},
    replacementSignUpEnabled: false,
    tenantLicenseLevel: null,
    publicmapColorOption: {},
    colorBlindMode: false,
    customerContact: {},
    materials: [],
    publicmapDisclaimer: {},
    enabledFeatures: {},
    tenantDisplayName: null,
  };
  const [state, setState] = useState(initialState);

  const loadPublicMaterials = async () => {
    const materials = await publicService.getPublicMaterials();

    setState((state) => ({
      ...state,
      materials,
    }));
  };

  const loadClientDetails = async () => {
    const customerContact = await publicService.getClientDetails();

    setState((state) => ({
      ...state,
      customerContact,
    }));
  };

  const loadPublicGISLayers = async () => {
    if (!tenantName) return;

    try {
      const publicGISLayers = await getPublicGISLayers(gisLayerURL, tenantName);

      setState((state) => ({
        ...state,
        publicGISLayers: publicGISLayers.filter(({ enabled }) => enabled),
      }));
    } catch (error) {
      SnackbarUtils.error('Error: could not load GIS layers');
    }
  };

  const loadPublicMapOptions = async () => {
    const result = await mapService.getPublicMapOptions();

    if (isEmpty(result)) {
      setState((state) => ({
        ...state,
        publicmapOption: DEFAULT_MAP_OPTIONS,
        publicmapColorOption: DEFAULT_PUBLIC_MAP_OPTIONS_COLOR,
        replacementSignUpEnabled: false,
      }));
    } else {
      setState((state) => ({
        ...state,
        publicmapOption: {
          latitude: result.latitude,
          longitude: result.longitude,
          zoom: result.zoom,
        },
        publicmapColorOption: {
          colorOption: result.colorOption,
          colorOptionString: result.colorOptionLabel,
        },
        colorBlindMode: result.colorOption === 2,
        replacementSignUpEnabled: result.replacementSignUpEnabled,
        tenantDisplayName: result.tenantDisplayName,
      }));
    }
  };

  const loadPublicMapDisclaimer = async () => {
    const publicmapDisclaimer = await mapService.getPublicMapDisclaimer();

    setState((state) => ({
      ...state,
      publicmapDisclaimer,
    }));
  };

  const featuresToCheck = [FEATURE_CONSTANTS.REPLACEMENT, FEATURE_CONSTANTS.CUSTOMER_PORTAL];
  const loadEnabledFeatures = async () => {
    const featureCalls = {};
    Object.values(FEATURE_CONSTANTS)
      .filter((featureValue) => featuresToCheck.includes(featureValue))
      .forEach((featureValue) => {
        featureCalls[featureValue] = publicService.getApplicationStatus(featureValue);
      });
    const enabledFeatures = await hash(featureCalls, true);

    setState((state) => ({
      ...state,
      enabledFeatures: enabledFeatures,
    }));
  };

  const loadTenantLicenseLevel = async () => {
    const tenantLicenseLevel = await publicService.getTenantLicenseLevel();

    setState((state) => ({
      ...state,
      tenantLicenseLevel,
    }));
  };

  useEffect(() => {
    loadPublicMapOptions();
    loadPublicGISLayers();
    loadClientDetails();
    loadPublicMaterials();
    loadPublicMapDisclaimer();
    loadEnabledFeatures();
    loadTenantLicenseLevel();
  }, [apiUrl, tenantName]);

  const setMapView = (mapView) => {
    setState((state) => ({
      ...state,
      publicmapOption: mapView,
    }));
  };

  const toggleColorBlindMode = () => {
    setState((state) => ({
      ...state,
      colorBlindMode: !state.colorBlindMode,
    }));
  };

  const providerValue = {
    ...state,
    apiUrl,
    gisLayerURL,
    setMapView,
    tenantName,
    toggleColorBlindMode,
  };

  return <PublicmapContext.Provider value={providerValue}>{children}</PublicmapContext.Provider>;
};

export default PublicmapContextProvider;
